import { useAlertasSdcQuery } from 'graphql/hooks.generated'
import { AlertasSdcQuery, CondutaSdc, GrupoSdcDengueEnum, SdcInput } from 'graphql/types.generated'
import { useLocalStorage, UseStorageOptions } from 'hooks/useStorage'
import React, { useCallback, useEffect, useMemo } from 'react'
import { createContext, useContext, useState } from 'react'

import { SdcDetalhesModal } from './components/SdcDetalhesModal'
import { AlertaSuporteDecisaoClinica, isAlertaSdc, isCondutaSdc, SdcAtendimentoValues } from './model-sdc'

interface EventosSdcCache {
  alertasSdc: AlertaSuporteDecisaoClinica[]
  condutasSdc: CondutaSdc[]
}

interface AlertasSdcProviderProps {
  cacheKey?: string
  children: React.ReactNode
  options?: UseStorageOptions<EventosSdcCache>
}

export interface SdcContextModel {
  alertasSdc: AlertaSuporteDecisaoClinica[]
  condutasSdc: CondutaSdc[]
  hasAlertaNaoLido: boolean
  hasCondutaAplicada: boolean
  loading: boolean
  setModalOpen(value: boolean): void
  setAlertaSelecionado(alerta?: AlertaSuporteDecisaoClinica): void
  setAlertasSdc(alerta: AlertaSuporteDecisaoClinica[]): void
  setSdcAtendimentoValues(value: SdcAtendimentoValues): void
  setGrupoDengue(grupoDengue: GrupoSdcDengueEnum): void
  marcarTodosAlertasComoLidos(): void
  marcarAlertaComoLido(alerta: AlertaSuporteDecisaoClinica): void
  marcarTodosAlertasComoExibidos(): void
  marcarAlertaComoExibido(alerta: AlertaSuporteDecisaoClinica): void
  marcarCondutaComoAplicada(conduta: CondutaSdc): void
  deleteCache(): void
}

const AlertasSdcContext = createContext<SdcContextModel>(undefined)

const CACHE_KEY_EVENTOS_SDC = 'EVENTOS_SDC|'

// TODO@CODEBENDERS refatorar e modularizar este hook pois está com muitas responsabilidades que podem ser segmentadas #25073
export const AlertasSdcProvider = (props: AlertasSdcProviderProps) => {
  const { children, cacheKey, options } = props

  const [alertasSdc, setAlertasSdc] = useState<AlertaSuporteDecisaoClinica[]>([])
  const [condutasSdc, setCondutasSdc] = useState<CondutaSdc[]>([])
  const [isModalOpen, setModalOpen] = useState(false)
  const [alertaSelecionado, setAlertaSelecionado] = useState<AlertaSuporteDecisaoClinica>()
  const [hasAlertaNaoLido, setHasAlertaNaoLido] = useState(false)
  const [hasCondutaAplicada, setHasCondutaAplicada] = useState(false)
  const [sdcAtendimentoValues, setSdcAtendimentoValues] = useState<SdcAtendimentoValues>()
  const [grupoDengue, setGrupoDengue] = useState<GrupoSdcDengueEnum>(null)

  const [getCachedEventosSdc, setCachedEventosSdc, deleteCachedEventosSdc] = useLocalStorage<EventosSdcCache>(
    CACHE_KEY_EVENTOS_SDC + cacheKey,
    { ...(options ?? {}), checkVersionCompatibility: true }
  )

  const getCachedAlertas = useCallback(() => {
    const eventosSdc = getCachedEventosSdc()
    return eventosSdc?.alertasSdc ?? []
  }, [getCachedEventosSdc])

  const getCachedCondutasAplicadas = useCallback(() => {
    const eventosSdc = getCachedEventosSdc()
    return eventosSdc?.condutasSdc ?? []
  }, [getCachedEventosSdc])

  const setCachedAlertas = useCallback(
    (alertas: AlertaSuporteDecisaoClinica[]) => {
      const eventosSdc = getCachedEventosSdc()
      setCachedEventosSdc({ ...eventosSdc, alertasSdc: alertas })
    },
    [getCachedEventosSdc, setCachedEventosSdc]
  )

  const setCachedCondutasAplicadas = useCallback(
    (condutas: CondutaSdc[]) => {
      const eventosSdc = getCachedEventosSdc()
      setCachedEventosSdc({ ...eventosSdc, condutasSdc: condutas })
    },
    [getCachedEventosSdc, setCachedEventosSdc]
  )

  const problemasCondicoes = sdcAtendimentoValues?.problemasCondicoes
  const isRegistroTardio = sdcAtendimentoValues?.isRegistroTardio
  const isBotaoPreNatalAtivo = sdcAtendimentoValues?.isBotaoPreNatalAtivo
  const alertasSdcInput: SdcInput = useMemo(
    () => ({
      problemaCondicaoSdcInput: problemasCondicoes?.map((problemaCondicao) => {
        return {
          cid10: problemaCondicao.cid?.codigo,
          ciap2: problemaCondicao.ciap?.codigo,
          situacaoProblema: problemaCondicao.situacaoProblema,
        }
      }),
      prontuarioId: sdcAtendimentoValues?.prontuarioId,
      dataInicioAtendimentoProfissional: sdcAtendimentoValues?.atendProfData,
      grupoDengue,
      pesoUltimas24Horas:
        sdcAtendimentoValues?.pesoUltimas24Horas != null
          ? parseFloat(sdcAtendimentoValues?.pesoUltimas24Horas?.replace(',', '.'))
          : null,
      isGravidezMultipla: sdcAtendimentoValues?.isGravidezMultipla,

      isRegistroTardio: isRegistroTardio,
      isBotaoPreNatalAtivo: isBotaoPreNatalAtivo,
    }),
    [problemasCondicoes, sdcAtendimentoValues, grupoDengue, isRegistroTardio, isBotaoPreNatalAtivo]
  )

  useEffect(() => {
    const cachedAlertas = getCachedAlertas() ?? []
    const cachedCondutasAplicadas = getCachedCondutasAplicadas() ?? []

    setAlertasSdc(cachedAlertas)
    setHasCondutaAplicada(cachedCondutasAplicadas.length > 0)
  }, [getCachedAlertas, getCachedCondutasAplicadas])

  useEffect(() => {
    setHasAlertaNaoLido(alertasSdc?.some((alerta) => !alerta.visualizado))
  }, [alertasSdc])

  useEffect(() => {
    alertasSdc.isEmpty() ? setAlertaSelecionado(null) : !alertaSelecionado && setAlertaSelecionado(alertasSdc[0])
  }, [alertaSelecionado, alertasSdc])

  const { loading } = useAlertasSdcQuery({
    variables: { input: alertasSdcInput },
    fetchPolicy: 'cache-and-network',
    skip: !sdcAtendimentoValues,
    onCompleted: (data: AlertasSdcQuery) => {
      if (!!sdcAtendimentoValues) {
        const eventosSdc = data?.eventosSdc ?? []
        const novosAlertas = filterAlertas(eventosSdc)
        const novasCondutas = filterCondutas(eventosSdc)

        const cachedAlertas = getCachedAlertas() ?? []

        const alertasExistentes = new Map(
          [...(alertasSdc ?? []), ...(cachedAlertas ?? [])].map((alerta) => [alerta.id, alerta])
        )

        const alertasAtualizados = atualizarAlertas(novosAlertas, alertasExistentes)

        setAlertasSdc(alertasAtualizados as AlertaSuporteDecisaoClinica[])
        setCondutasSdc(novasCondutas as CondutaSdc[])
        !!alertasAtualizados && setCachedAlertas(alertasAtualizados)
      }
    },
  })

  const atualizarAlertas = useCallback(
    (novosAlertas, alertasExistentes) =>
      (novosAlertas ?? []).map((novoAlerta) => {
        const alertaExistente = alertasExistentes.get(novoAlerta.id)

        if (!alertaExistente) {
          setHasAlertaNaoLido(true)
          return { ...novoAlerta, visualizado: false, exibido: false }
        }

        return alertaExistente
      }),
    []
  )

  const marcarAlertaComoExibido = useCallback(
    (alerta: AlertaSuporteDecisaoClinica) => {
      alerta.exibido = true
      !!alertasSdc && setCachedAlertas(alertasSdc)
    },
    [alertasSdc, setCachedAlertas]
  )

  const marcarTodosAlertasComoExibidos = useCallback(() => {
    alertasSdc?.forEach((alerta) => {
      if (alerta.notificar) alerta.exibido = true
    })
    !!alertasSdc && setCachedAlertas(alertasSdc)
  }, [alertasSdc, setCachedAlertas])

  const marcarAlertaComoLido = useCallback(
    (alerta: AlertaSuporteDecisaoClinica) => {
      alerta.visualizado = true
      alerta.exibido = true
      setHasAlertaNaoLido(alertasSdc?.some((alerta) => !alerta.visualizado))
      !!alertasSdc && setCachedAlertas(alertasSdc)
    },
    [alertasSdc, setCachedAlertas]
  )

  const marcarTodosAlertasComoLidos = useCallback(() => {
    alertasSdc?.forEach((alerta) => {
      alerta.visualizado = true
      alerta.exibido = true
    })
    setHasAlertaNaoLido(false)
    !!alertasSdc && setCachedAlertas(alertasSdc)
  }, [alertasSdc, setCachedAlertas])

  const marcarCondutaComoAplicada = useCallback(
    (conduta: CondutaSdc) => {
      setHasCondutaAplicada(true)
      const cachedCondutasAplicadas = getCachedCondutasAplicadas() ?? []
      setCachedCondutasAplicadas([...cachedCondutasAplicadas, conduta])
    },
    [getCachedCondutasAplicadas, setCachedCondutasAplicadas]
  )

  const value: SdcContextModel = useMemo(
    () => ({
      alertasSdc,
      condutasSdc,
      hasAlertaNaoLido,
      hasCondutaAplicada,
      setModalOpen,
      setAlertaSelecionado,
      setAlertasSdc,
      setSdcAtendimentoValues,
      setGrupoDengue,
      marcarTodosAlertasComoLidos,
      marcarAlertaComoLido,
      marcarTodosAlertasComoExibidos,
      marcarAlertaComoExibido,
      marcarCondutaComoAplicada,
      deleteCache: deleteCachedEventosSdc,
      loading,
    }),
    [
      alertasSdc,
      condutasSdc,
      hasAlertaNaoLido,
      hasCondutaAplicada,
      marcarAlertaComoExibido,
      marcarAlertaComoLido,
      marcarCondutaComoAplicada,
      marcarTodosAlertasComoExibidos,
      marcarTodosAlertasComoLidos,
      deleteCachedEventosSdc,
      loading,
    ]
  )

  return (
    <AlertasSdcContext.Provider value={value}>
      <SdcDetalhesModal
        isModalOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        alertaSelecionado={alertaSelecionado}
      />
      {children}
    </AlertasSdcContext.Provider>
  )
}

const filterAlertas = (eventosSdc: { __typename?: string }[]): AlertaSuporteDecisaoClinica[] =>
  eventosSdc?.filter(isAlertaSdc)

const filterCondutas = (eventosSdc: { __typename?: string }[]): CondutaSdc[] => eventosSdc?.filter(isCondutaSdc)

export const cardInfoSdc: AlertaSuporteDecisaoClinica = {
  id: 'avisos_card_id',
  titulo: 'Suporte à decisão clínica no PEC',
  subtitulo: 'Mais agilidade e segurança no atendimento',
  notificar: false,
  visualizado: true,
  exibido: true,
  conteudoPersonalizado:
    'O  PEC agora conta com a funcionalidade de Suporte à Decisão Clínica, desenvolvida para auxiliar profissionais de saúde na condução de atendimentos de forma mais ágil, segura e alinhada às melhores práticas recomendadas pelo Ministério da Saúde.',
  comoFunciona:
    '<ul><li><strong>Alertas de apoio à tomada de decisão:</strong> Com base nas informações clínicas e avaliação inseridas pelo profissional, o sistema pode gerar alertas e sugerir possíveis diagnósticos, além de recomendar a conduta adequada, fornecendo suporte para decisões mais seguras e embasadas.</li><li><strong>Automatização de condutas:</strong> O sistema disponibiliza condutas automatizadas com base em protocolos clínicos atualizados. O profissional pode optar por aplicar a conduta na totalidade ou em partes, editando as informações conforme necessário para cada caso específico.</li><li>É fundamental que o profissional avalie se as informações fornecidas pelo SDC se aplicam ao caso clínico do cidadão, pois as recomendações e condutas não têm caráter prescritivo.</li></ul><br><p>Até o momento foram implementados alertas focados em casos de dengue. A funcionalidade será expandida para incluir outros protocolos clínicos, proporcionando um suporte cada vez mais completo nos atendimentos.</p>',
}

export const useAlertasSdc = () => useContext(AlertasSdcContext)
